import {ApiClientError} from '@/api/client';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {RoleSelector} from '@/components/team/RoleSelector';
import {Spinner} from '@/components/ui/Spinner';
import {useEffect, useState} from 'react';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {useRemoveTeamMember, useResendInvite, useRetrySyncForMember} from '@/hooks/useRemoveTeamMember';
import {useSites} from '@/hooks/useSites';
import {useTeamMember} from '@/hooks/useTeamMember';
import {useUpdateTeamMember} from '@/hooks/useUpdateTeamMember';
import type {SiteId, TeamRole} from '@/types/api';

const SYNC_STATUS_CONFIG = {
    synced: {color: 'var(--studio-muted)', bg: 'var(--studio-soft)'},
    failed: {color: '#F50B23', bg: 'rgba(245, 11, 35, 0.1)'},
    pending: {color: '#EF5A2B', bg: 'rgba(239, 90, 43, 0.1)'},
    removed: {color: '#7C8B9A', bg: 'rgba(124, 139, 154, 0.1)'}
};

const STATUS_CONFIG = {
    pending: {color: '#EF5A2B', bg: 'rgba(239, 90, 43, 0.1)'},
    active: {color: 'var(--studio-muted)', bg: 'var(--studio-soft)'}
};

function BackButton({isCompact}: { isCompact: boolean }) {
    return (
        <Link
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: isCompact ? '1.2rem' : '1.3rem',
                fontWeight: 500,
                color: 'var(--tt-text-secondary)',
                textDecoration: 'none',
                marginBottom: isCompact ? 16 : 24,
                transition: 'color 0.15s ease'
            }}
            to="/team"
            onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--tt-text-primary)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--tt-text-secondary)';
            }}
        >
            <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
            </svg>
            Back to Team
        </Link>
    );
}

function SettingCard({children, style, isCompact}: { children: React.ReactNode; style?: React.CSSProperties; isCompact: boolean }) {
    return (
        <div className="studio-panel"
            style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, padding: isCompact ? 16 : 24, marginBottom: isCompact ? 16 : 24, transition: 'all 0.2s ease', ...style}}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = 'var(--tt-shadow-1)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            {children}
        </div>
    );
}

function SectionHeader({title, description, isCompact}: { title: string; description?: string; isCompact: boolean }) {
    return (
        <h3 style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 24px 0', paddingBottom: description ? 0 : 16, borderBottom: description ? 'none' : '1px solid var(--tt-divider)', lineHeight: 1.4, letterSpacing: '-.025em'}}>
            {title}
            {description && (
                <p
                    style={{
                        fontSize: isCompact ? '1.3rem' : '1.4rem',
                        fontWeight: 400,
                        color: 'var(--tt-text-secondary)',
                        margin: '6px 0 16px 0',
                        paddingBottom: 16,
                        borderBottom: '1px solid var(--tt-divider)',
                        lineHeight: 1.5
                    }}
                >
                    {description}
                </p>
            )}
        </h3>
    );
}

function FormLabel({children}: { children: React.ReactNode }) {
    return (
        <label
            style={{
                display: 'block',
                fontSize: '1.3rem',
                fontWeight: 600,
                color: 'var(--tt-text-primary)',
                marginBottom: 6
            }}
        >
            {children}
        </label>
    );
}

function DeleteConfirmDialog({
    isOpen,
    memberName,
    onConfirm,
    onCancel,
    isDeleting,
    isCompact
}: {
    isOpen: boolean;
    memberName: string;
    onConfirm: () => void;
    onCancel: () => void;
    isDeleting: boolean;
    isCompact: boolean;
}) {
    if (!isOpen) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'var(--tt-modal-backdrop)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: isCompact ? 16 : 0
            }}
            onClick={onCancel}
        >
            <div className="studio-panel"
                style={{background: 'var(--tt-card-bg)', borderRadius: 14, padding: isCompact ? 24 : 32, maxWidth: 420, width: '100%'}}
                onClick={e => e.stopPropagation()}
            >
                <div
                    style={{
                        width: isCompact ? 48 : 56,
                        height: isCompact ? 48 : 56,
                        borderRadius: '50%',
                        background: 'rgba(245, 11, 35, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                    }}
                >
                    <svg fill="none" height={isCompact ? 20 : 24} viewBox="0 0 24 24" width={isCompact ? 20 : 24}>
                        <path
                            d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="#F50B23"
                            strokeLinecap="round"
                            strokeWidth="2"
                        />
                    </svg>
                </div>

                <h3
                    style={{fontSize: isCompact ? '1.6rem' : '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 12px 0', textAlign: 'center', letterSpacing: '-.025em'}}
                >
                    Remove team member?
                </h3>

                <p
                    style={{
                        fontSize: isCompact ? '1.3rem' : '1.4rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 24px 0',
                        textAlign: 'center',
                        lineHeight: 1.5
                    }}
                >
                    <strong style={{color: 'var(--tt-text-primary)'}}>{memberName}</strong> will lose access to all assigned newsletters. Their Ghost user accounts will be deactivated.
                </p>

                <div style={{display: 'flex', gap: 12}}>
                    <button className="studio-control" disabled={isDeleting}
                        style={{flex: 1, height: isCompact ? 40 : 44, borderRadius: 8, border: '1px solid var(--tt-card-border)', background: 'var(--tt-card-bg)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: isDeleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button className="studio-control" disabled={isDeleting}
                        style={{flex: 1, height: isCompact ? 40 : 44, borderRadius: 8, border: 'none', background: '#F50B23', color: '#FFFFFF', fontSize: '13px', fontWeight: 500, cursor: isDeleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8}}
                        type="button"
                        onClick={onConfirm}
                    >
                        {isDeleting ? (
                            <>
                                <svg height="16" style={{animation: 'spin 1s linear infinite'}} viewBox="0 0 16 16" width="16">
                                    <circle cx="8" cy="8" fill="none" r="6" stroke="currentColor" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" strokeWidth="2"/>
                                </svg>
                                Removing...
                            </>
                        ) : (
                            'Remove'
                        )}
                    </button>
                </div>
            </div>
            <style>
                {`@keyframes spin { to { transform: rotate(360deg); } }`}
            </style>
        </div>
    );
}

export function EditTeamMemberPage() {
    const {memberId} = useParams<{ memberId: string }>();
    const navigate = useNavigate();
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;

    const {data: member, isLoading: memberLoading, isError, error} = useTeamMember(memberId!);
    const {data: sites, isLoading: sitesLoading} = useSites();
    const updateTeamMember = useUpdateTeamMember();
    const removeTeamMember = useRemoveTeamMember();
    const retrySyncForMember = useRetrySyncForMember();
    const resendInvite = useResendInvite();

    const [editingField, setEditingField] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [role, setRole] = useState<TeamRole>('Editor');
    const [selectedSiteIds, setSelectedSiteIds] = useState<SiteId[]>([]);
    const [updateError, setUpdateError] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const activeSites = sites?.filter(s => s.status === 'active') ?? [];

    useEffect(() => {
        if (member) {
            setName(member.name);
            setRole(member.role);
            setSelectedSiteIds(member.sites.filter(s => s.sync_status !== 'removed').map(s => s.tenant_id));
        }
    }, [member]);

    const handleSaveName = async () => {
        if (!name.trim()) {
            setUpdateError('Name is required.');
            return;
        }
        setUpdateError('');
        try {
            await updateTeamMember.mutateAsync({id: memberId!, data: {name: name.trim()}});
            setEditingField(null);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setUpdateError(err.message);
            } else {
                setUpdateError('Failed to update name.');
            }
        }
    };

    const handleSaveRole = async () => {
        setUpdateError('');
        try {
            await updateTeamMember.mutateAsync({id: memberId!, data: {role}});
            setEditingField(null);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setUpdateError(err.message);
            } else {
                setUpdateError('Failed to update role.');
            }
        }
    };

    const handleSaveSiteAccess = async () => {
        if (selectedSiteIds.length === 0) {
            setUpdateError('At least one newsletter must be selected.');
            return;
        }
        setUpdateError('');
        try {
            await updateTeamMember.mutateAsync({id: memberId!, data: {siteIds: selectedSiteIds}});
            setEditingField(null);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setUpdateError(err.message);
            } else {
                setUpdateError('Failed to update newsletter access.');
            }
        }
    };

    const handleRemoveMember = async () => {
        try {
            await removeTeamMember.mutateAsync(memberId!);
            navigate('/team');
        } catch (err) {
            setShowDeleteDialog(false);
            if (err instanceof ApiClientError) {
                setUpdateError(err.message);
            } else {
                setUpdateError('Failed to remove team member.');
            }
        }
    };

    const handleRetrySync = async () => {
        setUpdateError('');
        try {
            await retrySyncForMember.mutateAsync(memberId!);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setUpdateError(err.message);
            } else {
                setUpdateError('Failed to retry sync.');
            }
        }
    };

    const handleResendInvite = async () => {
        setUpdateError('');
        try {
            await resendInvite.mutateAsync(memberId!);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setUpdateError(err.message);
            } else {
                setUpdateError('Failed to resend invitation.');
            }
        }
    };

    const handleCancelEdit = () => {
        if (member) {
            setName(member.name);
            setRole(member.role);
            setSelectedSiteIds(member.sites.filter(s => s.sync_status !== 'removed').map(s => s.tenant_id));
        }
        setEditingField(null);
        setUpdateError('');
    };

    const handleToggleSite = (siteId: SiteId) => {
        if (selectedSiteIds.includes(siteId)) {
            setSelectedSiteIds(selectedSiteIds.filter(id => id !== siteId));
        } else {
            setSelectedSiteIds([...selectedSiteIds, siteId]);
        }
    };

    if (memberLoading || sitesLoading) {
        return (
            <div className="studio-content-page" style={{maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
                <BackButton isCompact={isCompact} />
                <div style={{display: 'flex', justifyContent: 'center', padding: isCompact ? 40 : 60}}>
                    <Spinner size={80} />
                </div>
            </div>
        );
    }

    if (isError || !member) {
        return (
            <div className="studio-content-page" style={{maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
                <BackButton isCompact={isCompact} />
                <SettingCard isCompact={isCompact}>
                    <div style={{textAlign: 'center', padding: isCompact ? 28 : 40}}>
                        <div
                            style={{
                                width: isCompact ? 48 : 56,
                                height: isCompact ? 48 : 56,
                                borderRadius: '50%',
                                background: 'rgba(245, 11, 35, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px'
                            }}
                        >
                            <svg fill="none" height={isCompact ? 20 : 24} viewBox="0 0 24 24" width={isCompact ? 20 : 24}>
                                <path
                                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                    stroke="#F50B23"
                                    strokeLinecap="round"
                                    strokeWidth="2"
                                />
                            </svg>
                        </div>
                        <h3 style={{fontSize: isCompact ? '1.5rem' : '1.6rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}>
                            Team member not found
                        </h3>
                        <p style={{fontSize: isCompact ? '1.2rem' : '1.3rem', color: 'var(--tt-text-secondary)', margin: 0}}>
                            {error instanceof Error ? error.message : 'The team member you are looking for does not exist.'}
                        </p>
                    </div>
                </SettingCard>
            </div>
        );
    }

    const inputStyle: React.CSSProperties = {
        width: '100%',
        height: isCompact ? 40 : 44,
        padding: isCompact ? '8px 12px' : '10px 14px',
        fontSize: isCompact ? '1.4rem' : '1.5rem',
        borderRadius: 8,
        border: '1px solid var(--tt-card-border)',
        color: 'var(--tt-text-primary)',
        background: 'var(--tt-card-bg)',
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit'
    };

    return (
        <div className="studio-content-page" style={{maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
            <BackButton isCompact={isCompact} />

            <div style={{display: 'flex', alignItems: isCompact ? 'flex-start' : 'center', gap: isCompact ? 12 : 16, marginBottom: isCompact ? 24 : 32}}>
                <div
                    style={{
                        width: isCompact ? 48 : 64,
                        height: isCompact ? 48 : 64,
                        borderRadius: '50%',
                        background: 'var(--tt-accent)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        fontSize: isCompact ? '1.8rem' : '2.2rem',
                        fontWeight: 600,
                        flexShrink: 0
                    }}
                >
                    {member.name.charAt(0).toUpperCase()}
                </div>
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: isCompact ? 8 : 10, flexWrap: 'wrap'}}>
                        <h1 className="studio-page-title">
                            {member.name}
                        </h1>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: isCompact ? '3px 8px' : '4px 10px',
                                borderRadius: 20,
                                fontSize: isCompact ? '1.0rem' : '1.1rem',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                lineHeight: 1,
                                whiteSpace: 'nowrap',
                                color: STATUS_CONFIG[member.status].color,
                                backgroundColor: STATUS_CONFIG[member.status].bg
                            }}
                        >
                            <span
                                style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: STATUS_CONFIG[member.status].color,
                                    flexShrink: 0
                                }}
                            />
                            {member.status === 'pending' ? 'Pending' : 'Active'}
                        </span>
                    </div>
                    <p style={{fontSize: isCompact ? '1.3rem' : '1.4rem', color: 'var(--tt-text-secondary)', margin: '4px 0 0 0', wordBreak: 'break-all'}}>
                        {member.email}
                    </p>
                </div>
            </div>

            {updateError && (
                <div
                    style={{
                        marginBottom: 20,
                        padding: '12px 16px',
                        background: 'rgba(245, 11, 35, 0.08)',
                        borderRadius: 8,
                        color: '#C9201C',
                        fontSize: '1.3rem',
                        lineHeight: 1.5
                    }}
                >
                    {updateError}
                </div>
            )}

            {member.status === 'pending' && (
                <SettingCard isCompact={isCompact} style={{background: 'rgba(178, 80, 0, 0.08)', border: '1px solid rgba(178, 80, 0, 0.2)'}}>
                    <div style={{display: 'flex', alignItems: isCompact ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 12 : 0}}>
                        <div>
                            <h3 style={{fontSize: isCompact ? '1.3rem' : '1.4rem', fontWeight: 500, color: '#B25000', margin: '0 0 4px 0', letterSpacing: '-.025em'}}>
                                Invitation pending
                            </h3>
                            <p style={{fontSize: isCompact ? '1.2rem' : '1.3rem', color: 'var(--tt-text-secondary)', margin: 0}}>
                                This team member hasn&apos;t accepted their invitation yet.
                            </p>
                        </div>
                        <button className="studio-control" disabled={resendInvite.isLoading}
                            style={{height: isCompact ? 34 : 36, padding: isCompact ? '0 14px' : '0 16px', borderRadius: 8, border: 'none', background: '#B25000', color: '#FFFFFF', fontSize: '13px', fontWeight: 500, cursor: resendInvite.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0}}
                            type="button"
                            onClick={handleResendInvite}
                        >
                            {resendInvite.isLoading ? (
                                <>
                                    <svg height="14" style={{animation: 'spin 1s linear infinite'}} viewBox="0 0 16 16" width="14">
                                        <circle cx="8" cy="8" fill="none" r="6" stroke="currentColor" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" strokeWidth="2"/>
                                    </svg>
                                    Sending...
                                </>
                            ) : (
                                'Resend invitation'
                            )}
                        </button>
                    </div>
                </SettingCard>
            )}

            <SettingCard isCompact={isCompact}>
                <SectionHeader description="Basic information about this team member." isCompact={isCompact} title="Profile" />

                <div style={{marginBottom: 20}}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                        <FormLabel>Name</FormLabel>
                        {editingField !== 'name' && (
                            <button style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--tt-accent)',
                                fontSize: '1.2rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                fontFamily: 'inherit'
                            }}
                            type="button"
                            onClick={() => setEditingField('name')}
                            >
                                Edit
                            </button>
                        )}
                    </div>
                    {editingField === 'name' ? (
                        <div>
                            <input className="studio-field-control"
                                style={inputStyle}
                                type="text"
                                value={name}
                                autoFocus
                                onChange={e => setName(e.target.value)}
                            />
                            <div style={{display: 'flex', gap: 8, marginTop: 12}}>
                                <button className="studio-control studio-control-primary" disabled={updateTeamMember.isLoading}
                                    style={{cursor: updateTeamMember.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                                    type="button"
                                    onClick={handleSaveName}
                                >
                                    {updateTeamMember.isLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button className="studio-control" disabled={updateTeamMember.isLoading}
                                    style={{height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid var(--tt-card-border)', background: 'var(--tt-card-bg)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: updateTeamMember.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                                    type="button"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p style={{fontSize: '1.5rem', color: 'var(--tt-text-primary)', margin: 0}}>{member.name}</p>
                    )}
                </div>

                <div>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                        <FormLabel>Email</FormLabel>
                    </div>
                    <p style={{fontSize: '1.5rem', color: 'var(--tt-text-secondary)', margin: 0}}>{member.email}</p>
                    <p style={{fontSize: '1.2rem', color: 'var(--tt-text-tertiary)', margin: '4px 0 0 0'}}>
                        Email cannot be changed after invitation.
                    </p>
                </div>
            </SettingCard>

            <SettingCard isCompact={isCompact}>
                <SectionHeader description="Determines what this team member can do on assigned newsletters." isCompact={isCompact} title="Role" />

                <div>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                        <FormLabel>Role</FormLabel>
                        {editingField !== 'role' && (
                            <button style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--tt-accent)',
                                fontSize: '1.2rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                fontFamily: 'inherit'
                            }}
                            type="button"
                            onClick={() => setEditingField('role')}
                            >
                                Edit
                            </button>
                        )}
                    </div>
                    {editingField === 'role' ? (
                        <div>
                            <RoleSelector value={role} onChange={setRole} />
                            <div style={{display: 'flex', gap: 8, marginTop: 12}}>
                                <button className="studio-control studio-control-primary" disabled={updateTeamMember.isLoading}
                                    style={{cursor: updateTeamMember.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                                    type="button"
                                    onClick={handleSaveRole}
                                >
                                    {updateTeamMember.isLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button className="studio-control" disabled={updateTeamMember.isLoading}
                                    style={{height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid var(--tt-card-border)', background: 'var(--tt-card-bg)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: updateTeamMember.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                                    type="button"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <span
                            style={{
                                display: 'inline-block',
                                padding: '6px 12px',
                                borderRadius: 6,
                                fontSize: '1.4rem',
                                fontWeight: 500,
                                background: 'var(--tt-badge-bg)',
                                color: 'var(--tt-text-primary)'
                            }}
                        >
                            {member.role}
                        </span>
                    )}
                </div>
            </SettingCard>

            <SettingCard isCompact={isCompact}>
                <SectionHeader description="Newsletters this team member can access and their sync status." isCompact={isCompact} title="Newsletter Access" />

                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 12}}>
                    {editingField !== 'sites' && (
                        <button style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--tt-accent)',
                            fontSize: '1.2rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            fontFamily: 'inherit'
                        }}
                        type="button"
                        onClick={() => setEditingField('sites')}
                        >
                            Edit access
                        </button>
                    )}
                </div>

                {editingField === 'sites' ? (
                    <div>
                        <div style={{border: '1px solid var(--tt-card-border)', borderRadius: 8, maxHeight: 320, overflowY: 'auto'}}>
                            {activeSites.map((site, index) => (
                                <label
                                    key={site.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '14px 16px',
                                        cursor: 'pointer',
                                        background: selectedSiteIds.includes(site.id) ? 'rgba(239, 90, 43, 0.05)' : 'var(--tt-card-bg)',
                                        borderBottom: index < activeSites.length - 1 ? '1px solid var(--tt-card-border)' : 'none',
                                        transition: 'background 0.15s'
                                    }}
                                >
                                    <input
                                        checked={selectedSiteIds.includes(site.id)}
                                        style={{
                                            width: 18,
                                            height: 18,
                                            accentColor: 'var(--tt-accent)',
                                            cursor: 'pointer'
                                        }}
                                        type="checkbox"
                                        onChange={() => handleToggleSite(site.id)}
                                    />
                                    <div style={{flex: 1}}>
                                        <p style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: 0}}>
                                            {site.name}
                                        </p>
                                        <p style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', margin: '2px 0 0 0'}}>
                                            {site.slug}.tribetale.com
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </div>
                        {selectedSiteIds.length === 0 && (
                            <p style={{fontSize: '1.2rem', color: 'var(--tt-red)', margin: '8px 0 0 0'}}>
                                Please select at least one newsletter
                            </p>
                        )}
                        <div style={{display: 'flex', gap: 8, marginTop: 16}}>
                            <button className="studio-control studio-control-primary" disabled={updateTeamMember.isLoading || selectedSiteIds.length === 0}
                                style={{cursor: updateTeamMember.isLoading || selectedSiteIds.length === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', background: selectedSiteIds.length === 0 ? 'var(--tt-btn-disabled-bg)' : 'var(--tt-accent)'}}
                                type="button"
                                onClick={handleSaveSiteAccess}
                            >
                                {updateTeamMember.isLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button className="studio-control" disabled={updateTeamMember.isLoading}
                                style={{height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid var(--tt-card-border)', background: 'var(--tt-card-bg)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: updateTeamMember.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                                type="button"
                                onClick={handleCancelEdit}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{border: '1px solid var(--tt-card-border)', borderRadius: 8, maxHeight: 320, overflowY: 'auto'}}>
                        {member.sites.length === 0 ? (
                            <div style={{padding: 20, textAlign: 'center'}}>
                                <p style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)', margin: 0}}>
                                    No newsletters assigned yet.
                                </p>
                            </div>
                        ) : (
                            member.sites.map((site, index) => {
                                const siteInfo = activeSites.find(s => s.id === site.tenant_id);
                                return (
                                    <div key={site.tenant_id}

                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '14px 16px',
                                            borderBottom: index < member.sites.length - 1 ? '1px solid var(--tt-card-border)' : 'none'
                                        }}
                                    >
                                        <div>
                                            <p style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: 0}}>
                                                {site.site_name || siteInfo?.name || 'Unknown Newsletter'}
                                            </p>
                                            {siteInfo && (
                                                <p style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', margin: '2px 0 0 0'}}>
                                                    {siteInfo.slug}.tribetale.com
                                                </p>
                                            )}
                                        </div>
                                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    padding: '4px 10px',
                                                    borderRadius: 20,
                                                    fontSize: '1.1rem',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.05em',
                                                    textTransform: 'uppercase',
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                    color: SYNC_STATUS_CONFIG[site.sync_status].color,
                                                    backgroundColor: SYNC_STATUS_CONFIG[site.sync_status].bg
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: SYNC_STATUS_CONFIG[site.sync_status].color,
                                                        flexShrink: 0
                                                    }}
                                                />
                                                {site.sync_status === 'synced' && 'Synced'}
                                                {site.sync_status === 'failed' && 'Failed'}
                                                {site.sync_status === 'pending' && 'Pending'}
                                                {site.sync_status === 'removed' && 'Removed'}
                                            </span>
                                            {site.sync_status === 'failed' && (
                                                <button className="studio-control" disabled={retrySyncForMember.isLoading}
                                                    style={{height: 28, padding: '0 10px', borderRadius: 8, border: '1px solid var(--tt-card-border)', background: 'var(--tt-card-bg)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: retrySyncForMember.isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                                                    type="button"
                                                    onClick={handleRetrySync}
                                                >
                                                    {retrySyncForMember.isLoading ? 'Retrying...' : 'Retry'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </SettingCard>

            <SettingCard isCompact={isCompact} style={{borderColor: 'rgba(245, 11, 35, 0.2)'}}>
                <SectionHeader isCompact={isCompact} title="Danger Zone" />
                <div style={{display: 'flex', alignItems: isCompact ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 12 : 0}}>
                    <div>
                        <p style={{fontSize: isCompact ? '1.3rem' : '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 4px 0'}}>
                            Remove team member
                        </p>
                        <p style={{fontSize: isCompact ? '1.2rem' : '1.3rem', color: 'var(--tt-text-secondary)', margin: 0}}>
                            Revoke access to all newsletters and deactivate Ghost accounts.
                        </p>
                    </div>
                    <button className="studio-control" style={{height: isCompact ? 34 : 36, padding: isCompact ? '0 14px' : '0 16px', borderRadius: 8, border: '1px solid var(--tt-red)', background: 'var(--tt-card-bg)', color: 'var(--tt-red)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease', flexShrink: 0}}
                        type="button"
                        onClick={() => setShowDeleteDialog(true)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--tt-red)';
                            e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--tt-card-bg)';
                            e.currentTarget.style.color = 'var(--tt-red)';
                        }}
                    >
                        Remove
                    </button>
                </div>
            </SettingCard>

            <DeleteConfirmDialog
                isCompact={isCompact}
                isDeleting={removeTeamMember.isLoading}
                isOpen={showDeleteDialog}
                memberName={member.name}
                onCancel={() => setShowDeleteDialog(false)}
                onConfirm={handleRemoveMember}
            />

            <style>
                {`@keyframes spin { to { transform: rotate(360deg); } }`}
            </style>
        </div>
    );
}
